import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Task } from '@/types';

interface TaskDetailsProps {
  task: Task | null;
  open: boolean;
  onClose: () => void;
  onUpdate: (taskId: string, updates: Partial<Task>) => void;
}

export const TaskDetails: React.FC<TaskDetailsProps> = ({
  task,
  open,
  onClose,
  onUpdate
}) => {
  const [newComment, setNewComment] = useState('');

  if (!task) return null;

  const handleAddComment = () => {
    if (!newComment.trim()) return;

    const comment = {
      id: `comment-${Date.now()}`,
      userId: 'current-user', // In a real app, get from auth context
      content: newComment,
      timestamp: new Date().toISOString()
    };

    onUpdate(task.id, {
      comments: [...task.comments, comment]
    });

    setNewComment('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_process':
        return 'primary';
      case 'frozen':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Task Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {task.title}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={task.status.replace('_', ' ')}
                color={getStatusColor(task.status)}
                size="small"
              />
              <Chip
                label={task.priority}
                color={getPriorityColor(task.priority)}
                size="small"
              />
              <Chip
                label={task.category}
                variant="outlined"
                size="small"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1">
              {task.description}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Details
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                Assigned to: {task.assignedTo}
              </Typography>
              <Typography variant="body2">
                Due date: {format(new Date(task.dueDate), 'PPP')}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Comments
            </Typography>
            <List>
              {task.comments.map((comment) => (
                <ListItem key={comment.id} sx={{ px: 0 }}>
                  <ListItemText
                    primary={comment.content}
                    secondary={format(new Date(comment.timestamp), 'PPP p')}
                    primaryTypographyProps={{
                      variant: 'body2'
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption'
                    }}
                  />
                </ListItem>
              ))}
            </List>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                multiline
                rows={2}
              />
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Send
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};